// ════════════════════════════════════════════════════════════════════════════
// 🎯 CustomerPicker — Phase 3.1 Phone-first customer search & inline create
// ════════════════════════════════════════════════════════════════════════════
// Behavior:
//   - Search box debounces 250ms → /api/customers?q=...
//   - If exact phone match exists, shows it as primary card
//   - If no result and query looks like a phone, shows "Create new" form
//   - Selecting a customer fires onPick(customer)
// ════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  X, Search, UserPlus, Phone, User, Mail, Calendar, Loader2,
  CheckCircle2, Award, Trash2, Crown, TrendingUp,
} from 'lucide-react';
import { Haptics } from '../../utils/haptics';

export interface CustomerLite {
  id: number;
  name: string;
  phone: string;
  email?: string | null;
  points: number | string;
  totalSpent: number | string;
  totalVisits: number;
  lastVisitAt?: string | null;
  birthday?: string | null;
  notes?: string | null;
  createdAt?: string;
  address?: string | null;
  customFieldValue?: string | null;
  storeCredit?: number | string;
}

type KeyType = string;

interface CustomerPickerProps {
  open: boolean;
  storeApiKey: KeyType;
  onClose: () => void;
  onPick: (customer: CustomerLite) => void;
  customFieldLabel?: string;
  customFieldPlaceholder?: string;
  customFieldRequired?: boolean;
}

const formatRs = (n: number) =>
  'Rs. ' + n.toLocaleString('en-LK', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const looksLikePhone = (s: string) => /^[+0-9][0-9\s-]{5,}$/.test(s.trim());

const CustomerPicker: React.FC<CustomerPickerProps> = ({
  open,
  storeApiKey,
  onClose,
  onPick,
  customFieldLabel = 'Vehicle Number',
  customFieldPlaceholder = 'Enter Vehicle No',
  customFieldRequired = false
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CustomerLite[]>([]);
  const [topCustomers, setTopCustomers] = useState<CustomerLite[]>([]);
  const [topLoading, setTopLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [createMode, setCreateMode] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '', birthday: '', notes: '', address: '', customFieldValue: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const debounceRef = useRef<number | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // 🛡️ Phase 4 — Unified RBAC Headers Helper
  const getHeaders = () => {
    const token = localStorage.getItem('kangavena:cashierToken') || '';
    return {
      'x-store-api-key': storeApiKey,
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  };

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults([]);
      setTopCustomers([]);
      setCreateMode(false);
      setError('');
      setForm({ name: '', phone: '', email: '', birthday: '', notes: '', address: '', customFieldValue: '' });
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      const q = query.trim();
      setLoading(true);
      try {
        const url = q
          ? `/api/customers?q=${encodeURIComponent(q)}&sort=spend&limit=20`
          : `/api/customers?sort=spend&limit=20`;
        const res = await fetch(url, { headers: getHeaders() });
        const data = await res.json();
        if (res.ok) setResults(data.customers || []);
      } catch { /* network — silent */ }
      finally { setLoading(false); }
    }, 250);
    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current); };
  }, [query, open, storeApiKey]);

  // 🔥 Top customers — load when modal opens (no query) / no search results
  useEffect(() => {
    if (!open || query.trim()) {
      setTopCustomers([]);
      return;
    }
    setTopLoading(true);
    fetch(`/api/customers?sort=spend&limit=5`, { headers: getHeaders() })
      .then((r) => r.json())
      .then((data) => setTopCustomers(data.customers || []))
      .catch(() => { /* silent */ })
      .finally(() => setTopLoading(false));
  }, [open, query, storeApiKey]);

  const submitCreate = async () => {
    setError('');
    if (!form.name.trim() || !form.phone.trim()) { setError('Name and phone are required'); return; }
    if (customFieldRequired && !form.customFieldValue.trim()) {
      setError(`${customFieldLabel} is required`);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/customers', {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        // 409 returns existing customer — offer to pick it
        if (res.status === 409 && data.customer) {
          Haptics.fire('warn');
          onPick(data.customer);
          return;
        }
        setError(data.error || 'Failed to create customer');
        return;
      }
      Haptics.fire('success');
      onPick(data.customer);
    } catch (err: any) {
      setError(err?.message || 'Network error');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const showCreateHint = !loading && query.length >= 4 && results.length === 0;

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="cp-bg"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10001] bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          key="cp-card"
          initial={{ y: 400, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 400, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          className="w-full sm:max-w-lg max-h-[92vh] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="shrink-0 flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100">
            <div>
              <h3 className="text-[14px] font-black text-slate-800 leading-tight">Attach customer</h3>
              <p className="text-[10px] text-slate-500">Earn loyalty points on this sale</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors"
              aria-label="Close"
            >
              <X size={15} />
            </button>
          </div>

          {!createMode ? (
            <>
              {/* Search */}
              <div className="shrink-0 px-4 pt-3 pb-2">
                <div className="relative">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by phone or name…"
                    className="w-full pl-9 pr-9 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none text-[13px] font-mono text-slate-800 placeholder:text-slate-400"
                  />
                  {loading && <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />}
                </div>
              </div>

              {/* Results */}
              <div className="flex-1 overflow-y-auto px-4 pb-2 space-y-1.5 font-sans">
                {/* 🔥 Top Customers — shown when no search query */}
                {!query && topCustomers.length > 0 && (
                  <>
                    <div className="text-[9px] font-black uppercase text-amber-600 tracking-wider pb-1 pt-1 flex items-center gap-1.5">
                      <Crown size={11} /> වැඩිපුරම ගනුදෙනු කරන පාරිභෝගිකයින්
                    </div>
                    {topCustomers.map((c) => (
                      <motion.button
                        key={`top-${c.id}`}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => { Haptics.fire('tap'); onPick(c); }}
                        className="w-full flex items-center gap-3 p-3 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 hover:border-amber-400 text-left transition-all"
                      >
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 border-2 border-amber-300 flex items-center justify-center shrink-0 shadow-sm">
                          <Crown size={15} className="text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[12px] font-black text-slate-800 truncate">{c.name}</div>
                          <div className="text-[10px] text-slate-500 font-mono flex items-center gap-1.5 mt-0.5">
                            <Phone size={9} /> {c.phone}
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="flex items-center gap-1 text-[11px] font-black text-emerald-600">
                            <TrendingUp size={11} /> {formatRs(Number(c.totalSpent))}
                          </div>
                          <div className="text-[9px] text-slate-400 font-mono">
                            {Number(c.points).toFixed(0)} pts · {c.totalVisits}v
                          </div>
                          {Number(c.storeCredit || 0) > 0 && (
                            <div className="text-[9px] font-bold text-emerald-600 font-mono mt-0.5">
                              💰 {formatRs(Number(c.storeCredit))} credit
                            </div>
                          )}
                        </div>
                      </motion.button>
                    ))}
                    <div className="border-t border-slate-100 pt-1.5 mt-1"></div>
                  </>
                )}

                {topLoading && !query && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 size={14} className="animate-spin text-amber-500" />
                    <span className="text-[10px] text-slate-400 ml-2">Loading top customers…</span>
                  </div>
                )}

                {/* Recent customers or search results */}
                {results.length === 0 && !query && !topLoading && topCustomers.length === 0 && (
                  <div className="text-center py-8 text-[11px] text-slate-400">
                    No customers registered yet. Type to create one.
                  </div>
                )}

                {results.length > 0 && !query && (
                  <div className="text-[9px] font-black uppercase text-slate-400 tracking-wider pb-1.5 flex items-center gap-1">
                    <User size={10} /> Existing Customers (Top Spenders First)
                  </div>
                )}

                {query && results.length > 0 && (
                  <div className="text-[9px] font-black uppercase text-slate-400 tracking-wider pb-1.5 flex items-center gap-1">
                    <Search size={10} /> Search Results
                  </div>
                )}

                {results.map((c) => (
                  <motion.button
                    key={c.id}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => { Haptics.fire('tap'); onPick(c); }}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-100 bg-white hover:bg-brand-surface hover:border-brand-primary text-left transition-all"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-brand-primary-100 to-brand-surface border border-brand-surface-border flex items-center justify-center shrink-0">
                      <User size={15} className="text-brand-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-black text-slate-800 truncate">{c.name}</div>
                      <div className="text-[10px] text-slate-500 font-mono flex items-center gap-1.5 mt-0.5">
                        <Phone size={9} /> {c.phone}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="flex items-center gap-1 text-[11px] font-black text-amber-500">
                        <Award size={11} /> {Number(c.points).toFixed(0)}
                      </div>
                      <div className="text-[9px] text-slate-400 font-mono">
                        {formatRs(Number(c.totalSpent))} · {c.totalVisits}v
                      </div>
                      {Number(c.storeCredit || 0) > 0 && (
                        <div className="text-[9px] font-bold text-emerald-600 font-mono mt-0.5">
                          💰 {formatRs(Number(c.storeCredit))} credit
                        </div>
                      )}
                    </div>
                  </motion.button>
                ))}

                {showCreateHint && (
                  <button
                    onClick={() => {
                      Haptics.fire('tap');
                      setForm((f) => ({
                        ...f,
                        phone: looksLikePhone(query) ? query.trim() : f.phone,
                        name: !looksLikePhone(query) ? query.trim() : f.name,
                      }));
                      setCreateMode(true);
                    }}
                    className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-brand-primary/40 bg-brand-surface/40 text-brand-primary hover:bg-brand-surface text-[12px] font-black uppercase tracking-wider transition-all"
                  >
                    <UserPlus size={13} /> Create new customer
                  </button>
                )}
              </div>

              {/* Bottom create CTA always available */}
              <div className="shrink-0 px-4 pt-2 pb-4 border-t border-slate-100">
                <button
                  onClick={() => { Haptics.fire('tap'); setCreateMode(true); }}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 text-[11px] font-black uppercase tracking-wider transition-all"
                >
                  <UserPlus size={12} /> New customer
                </button>
              </div>
            </>
          ) : (
            // ─── Create mode ─────────────────────────────────────────────
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                  <User size={10} /> Name *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Full name"
                  className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none text-[13px] text-slate-800"
                  autoFocus
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                  <Phone size={10} /> Phone *
                </label>
                <input
                  type="tel"
                  inputMode="tel"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  placeholder="077 123 4567"
                  className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none text-[13px] font-mono text-slate-800"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                    <Mail size={10} /> Email
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="optional"
                    className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none text-[12px] text-slate-800"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                    <Calendar size={10} /> Birthday
                  </label>
                  <input
                    type="date"
                    value={form.birthday}
                    onChange={(e) => setForm((f) => ({ ...f, birthday: e.target.value }))}
                    className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none text-[12px] font-mono text-slate-800"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={2}
                  placeholder="Allergies, preferences…"
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none text-[12px] text-slate-700 resize-none"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Address</label>
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  placeholder="Enter address"
                  className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none text-[13px] text-slate-800"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  {customFieldLabel} {customFieldRequired ? '*' : ''}
                </label>
                <input
                  type="text"
                  value={form.customFieldValue}
                  onChange={(e) => setForm((f) => ({ ...f, customFieldValue: e.target.value }))}
                  placeholder={customFieldPlaceholder}
                  className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/20 outline-none text-[13px] text-slate-800"
                />
              </div>

              {error && (
                <div className="text-[11px] font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                  {error}
                </div>
              )}

              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={() => setCreateMode(false)}
                  className="flex-1 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 text-[11px] font-black uppercase tracking-wider transition-all"
                >
                  Back
                </button>
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={submitCreate}
                  disabled={submitting || !form.name.trim() || !form.phone.trim()}
                  className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white text-[11px] font-black uppercase tracking-wider shadow-lg shadow-emerald-500/25 disabled:opacity-40 flex items-center justify-center gap-1.5"
                >
                  {submitting ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                  Save & Attach
                </motion.button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default CustomerPicker;
